import { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UIProvider, useUI } from './context/UIContext';
import { TerritoriesProvider } from './context/TerritoriesContext';
import { ConfigProvider } from './context/ConfigContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './views/auth/Login';
import Brandbar from './components/Brandbar';
import Topbar from './components/Topbar';
import Sidebar from './components/Sidebar';
import ScopeBanner from './components/ScopeBanner';
import LeadDrawer from './components/LeadDrawer';
import LogVisitModal from './components/LogVisitModal';
import BottomNav from './components/BottomNav';
import DesktopOnlyNotice from './components/DesktopOnlyNotice';
import { useIsMobile } from './hooks/useIsMobile';
import { canSee, DEFAULT_VIEW } from './components/nav';
import { BRAND } from './config/brands';

import MyDay from './views/activity/MyDay';
import Scoreboard from './views/activity/Scoreboard';
import LeadsPipeline from './views/leads/LeadsPipeline';
import CustomerVisits from './views/visits/CustomerVisits';
import Requests from './views/requests/Requests';
import TerritoryMap from './views/map/TerritoryMap';
import UserManagement from './views/admin/UserManagement';
import CsvImport from './views/admin/CsvImport';
import Settings from './views/admin/Settings';
import Stores from './views/admin/Stores';
import TerritorySettings from './views/admin/TerritorySettings';
import AuditLog from './views/admin/AuditLog';
import SalesJourney from './views/admin/SalesJourney';
import Tenants from './views/admin/Tenants';

// Build fingerprint injected by Vite (vite.config define); 'dev' under raw tooling.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

// Centralized page-head copy (mockup labels — satisfies T-UI-009). My Day renders
// its own head (Day/Week/Month + Log Visit + stat cards) and is excluded here.
const PAGE_META = {
  leads: { title: 'Leads & Pipeline', sub: 'Your tenant-scoped pipeline — table or Kanban' },
  visits: { title: 'Customer Visits', sub: "Won accounts on a recurring service cadence — what's next, so nothing drops" },
  requests: { title: 'Customer Requests & Feedback', sub: 'Requests and feedback on the customer timeline' },
  map: { title: 'Territory Map', sub: 'Square quadrants · hub circle inscribed · click a pin to open the lead' },
  score: { title: 'Scoreboard', sub: 'Leading indicators — activity, not just outcomes' },
  tenants: { title: 'Tenants', sub: 'Create a tenant + its owner, then switch in to continue setup (HQ Admin only)' },
  users: { title: 'User Management', sub: 'People in this tenant and what they can see' },
  stores: { title: 'Stores / Hubs', sub: 'How you add and move hubs (location & service radius)' },
  terr: { title: 'Territory Settings', sub: 'Quadrant polygons, colors and rollout' },
  import: { title: 'CSV Import', sub: 'Map columns, dedupe by Contact ID, write tenant-scoped leads' },
  journey: { title: 'Sales Journey', sub: 'Owner-only full lifecycle history of every lead — generate and export to CSV' },
  settings: { title: 'Settings', sub: 'Proof policy, reason codes and defaults for this tenant' },
  audit: { title: 'Audit Log', sub: 'Append-only, server-written record of every change' },
};

function PageHead({ title, sub, children }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      <span className="spacer" />
      {children}
    </div>
  );
}

function viewBody(view, navigate) {
  switch (view) {
    case 'today': return <MyDay />;
    case 'leads': return <LeadsPipeline />;
    case 'visits': return <CustomerVisits />;
    case 'requests': return <Requests />;
    case 'map': return <TerritoryMap />;
    case 'score': return <Scoreboard />;
    case 'tenants': return <Tenants />;
    case 'users': return <UserManagement />;
    case 'stores': return <Stores />;
    case 'terr': return <TerritorySettings />;
    case 'import': return <CsvImport />;
    case 'journey': return <SalesJourney />;
    case 'settings': return <Settings />;
    case 'audit': return <AuditLog />;
    default: return <MyDay />;
  }
}

// Authed shell — grid frame (brand/top/side/main) ported 1-to-1 from the mockup.
function AppShell() {
  const { role } = useAuth();
  const { openLogVisit } = useUI();
  const [view, setView] = useState(DEFAULT_VIEW);

  // After a demo-role switch the current view may no longer be permitted.
  const safeView = canSee(view, role) ? view : DEFAULT_VIEW;
  const navigate = (id) => setView(canSee(id, role) ? id : DEFAULT_VIEW);

  const meta = PAGE_META[safeView];

  const isMobile = useIsMobile();
  // Screens we optimized for mobile; others show a desktop-only notice on phones.
  const MOBILE_OK = new Set(['today', 'leads', 'visits', 'map']);
  const showDesktopNotice = isMobile && !MOBILE_OK.has(safeView);

  return (
    <div className="app-shell">
      <Brandbar />
      <Topbar onLogVisit={() => openLogVisit()} />
      <Sidebar view={safeView} role={role} onNavigate={navigate} />
      <main className="main">
        {showDesktopNotice ? (
          <DesktopOnlyNotice title={meta?.title || 'This screen'} />
        ) : safeView === 'today' ? (
          viewBody('today', navigate)
        ) : (
          <>
            {meta && <PageHead title={meta.title} sub={meta.sub} />}
            <ScopeBanner />
            {viewBody(safeView, navigate)}
          </>
        )}
        <footer style={{ textAlign: 'center', padding: 'var(--space-5) 0 0', color: 'var(--color-fg-faint)', fontSize: 'var(--text-caption)' }}>
          {BRAND.name} CRM {APP_VERSION}
        </footer>
      </main>
      {isMobile && <BottomNav view={safeView} role={role} onNavigate={navigate} onLogVisit={() => openLogVisit()} />}
      <LeadDrawer />
      <LogVisitModal />
    </div>
  );
}

// Decides login vs. app once auth state is known. Lives inside the provider.
function Gate() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--color-bg)', color: 'var(--color-fg-muted)', fontSize: 'var(--text-small)' }}>
        Loading…
      </div>
    );
  }
  return isAuthenticated ? <AppShell /> : <Login />;
}

// Provider wraps the entire render tree (ref: context-hook-needs-provider-at-every-render-site).
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UIProvider>
          <TerritoriesProvider>
            <ConfigProvider>
              <Gate />
            </ConfigProvider>
          </TerritoriesProvider>
        </UIProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
